/* eslint-disable no-undef */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getReduxActionErrorMessage, isReduxThunkRejectedAction } from '../core/utils';

import { ApiGatewayUrl } from '../../src/appsettings';
import ErrorException from "src/components/ErrorException";
import Rest from "../core/rest";
import { STATUS_TYPES } from '../core/constants';
import _ from 'lodash';
import { getTreeviewDataFormat } from '../slices/shared/getTreeviewDataFormat';
import log from 'loglevel';

const SLICE_NAME = "cameraACL";

export const getACLs = createAsyncThunk(
  'cameraACL/getACLs',
  async (request) => {
    try {
      const request1 = Rest.get(
        ApiGatewayUrl + '/singleacls?camera_id=' + request.deviceId
      );
      let ids = '';
      if (request.user_gp_ids.length > 0) {
        ids = request.user_gp_ids.join(",");
      }
      const request2 = Rest.get(ApiGatewayUrl + '/singleacls/filter?page=' + request.page + "&rows_per_page=" + request.rows_per_page +
        "&agency_id=" + request.agency_id + "&user_gp_ids=" + ids + "&filter_text=" + request.filter_text);
      const response = await Rest.getAll([request1, request2]);
      return JSON.stringify(response);
    }
    catch (ex) {
      ErrorException(ex.response);
    }
  }
);


export const updateACLs = createAsyncThunk(
  'cameraACL/updateACLs',
  async (info) => {
    try {
      const response = await Rest.post(ApiGatewayUrl + '/singleacls', info);
      return response.data;
    }
    catch (ex) {
      ErrorException(ex.response);
    }
  }
);

export const getACLCamerasByUser = createAsyncThunk(
  'cameraACL/getCameras',
  async () => {
    try {
      const response = await Rest.get(ApiGatewayUrl + '/videosensors');
      return response.data;
    }
    catch (ex) {
      ErrorException(ex.response);
    }
  });

export const searchCameras = createAsyncThunk(
  'cameraACL/searchCameras',
  async (filter_text) => {
    try {
      const response = await Rest.get(
        ApiGatewayUrl + '/videosensors/search?filter_text=' + filter_text
      );
      let result = { data: response.data, cameraName: filter_text }
      return result;
    }
    catch (ex) {
      ErrorException(ex.response);
    }
  }
);

export const filterUsers = createAsyncThunk(
  'cameraACL/filterUser',
  async (request) => {
    try {
      let ids = '';
      if (request.user_gp_ids.length > 0) {
        ids = request.user_gp_ids.join(",");
      }
      const response = await Rest.get(ApiGatewayUrl + '/singleacls/filter?page=' + request.page + "&rows_per_page=" + request.rows_per_page +
        "&agency_id=" + request.agency_id + "&user_gp_ids=" + ids + "&filter_text=" + request.filter_text);
      return response.data;
    }
    catch (ex) {
      ErrorException(ex.response);
    }
  }
);
export const cameraACLSlice = createSlice({
  name: SLICE_NAME,
  initialState: {
    cameras: [],
    cameraAcls: [],
    users: [],
    userGroups: [],
    selectedCameraId: 0,
    selectedCameraName: '',
    selectedCameraConrollable: 0,
    selectedCameraPrivacy: 0,
    userAcls: [],
    changed: false,
    isLoading: false,
    isAclRequesting: false,
    saving: false,
    total: 0,
    selectedAgencyId: 0,
    selectedUserGpIds: [],
    filteredText: '',
    searchText: '',
    defaultExpanded: [],
    treeViewCameras: [],
  },
  reducers: {
    saveSelectedCamera: (state, action) => {
      const payload = action.payload;
      log.info("reducers save selected camera => ", payload)
      state.selectedCameraId = payload.deviceId;
      state.selectedCameraName = payload.deviceName;
      state.selectedCameraConrollable = payload.controllable;
      state.selectedCameraPrivacy = payload.privacy
    },
    saveUserAcls: (state, action) => {
      state.userAcls = action.payload;
    },
    changedState: (state, action) => {
      state.changed = action.payload;
    },
    saveSelectedAgencyId: (state, action) => {
      state.selectedAgencyId = action.payload;
    },

    saveSelectedUserGpIds: (state, action) => {
      state.selectedUserGpIds = action.payload;
    },

    saveFilterText: (state, action) => {
      state.filteredText = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder

      .addCase(updateACLs.pending, (state) => {
        return { ...state, saving: true };
      })

      .addCase(updateACLs.fulfilled, (state) => {
        return {
          ...state,
          saving: false,
          message: "Your ACL record/s saved successfully.",
          status: 'success'
        };
      })

      .addCase(updateACLs.rejected, (state, action) => {
        return {
          ...state,
          message: action.error.message,
          status: 'error',
          saving: false
        };
      })

      .addCase(getACLs.pending, (state) => {
        return { ...state, isAclRequesting: true };
      })

      .addCase(getACLs.fulfilled, (state, action) => {
        const response = JSON.parse(action.payload);
        const acls = response[0].data.camera_acls;
        const users = response[1].data.users;
        let result = users.map((u) => ({
          ...acls.find((acl) => u.user_id === acl.user_id),
          ...u
        }));
        return {
          ...state,
          users: _.map(result, (x) => {

            return {
              user_role_id: x.user_role_id,
              user_id: x.user_id,
              email: x.email,
              user_group_id: x.user_group_id,
              user_group_name: x.user_group_name,
              agency_id: x.agency_id,
              video_sensor_id: x.video_sensor_id,
              live_view: x.live_view,
              play_back: x.play_back,
              recording: x.recording,
              download: x.download,
              ptz: x.ptz,
              access_expiry: x.access_expiry
            };
          }),
          userGroups: response[1].data.userGroups,
          total: response[1].data.total,
          isAclRequesting: false,
          selectedAgencyId: response[1].data.selectedAgencyId
        };
      })

      .addCase(getACLs.rejected, (state) => {
        return { ...state, isAclRequesting: false };
      })

      .addCase(getACLCamerasByUser.pending, (state) => {
        return { ...state, isLoading: true };
      })

      .addCase(getACLCamerasByUser.fulfilled, (state, action) => {
        let { agencyExpand, vgpExpanded, data } = getTreeviewDataFormat(action.payload);
        return {
          ...state,
          cameras: action.payload, /*flat data for override acl when open preset */
          treeViewCameras: data,
          defaultExpanded: [...agencyExpand, ...vgpExpanded],
          isLoading: false
        };
      })
      .addCase(getACLCamerasByUser.rejected, (state, action) => {
        return { ...state, message: action.error.message };
      })

      .addCase(searchCameras.fulfilled, (state, action) => {
        let { agencyExpand, vgpExpanded, data } = getTreeviewDataFormat(action.payload.data);
        return {
          ...state,
          cameras: action.payload.data, /*flat data for override acl when open preset */
          searchText: action.payload.cameraName,
          treeViewCameras: data,
          defaultExpanded: [...agencyExpand, ...vgpExpanded],
          isLoading: false
        };
      })

      .addCase(filterUsers.fulfilled, (state, action) => {
        return {
          ...state,
          users: action.payload.users,
          userGroups: action.payload.userGroups,
          total: action.payload.total,
          isLoading: false
        };
      })


      //handle rejected actions. 
      .addMatcher((action) => isReduxThunkRejectedAction(SLICE_NAME, action.type), (state, action) => {
        if (action.error && action.error.message === "Aborted") {
          return {
            ...state,
            isLoading: false,
            statusType: "",
            actionType: action.type?.split("/")[1],
            errorMessage: "",
          };
        } else {
          return {
            ...state,
            isLoading: false,
            actionType: action.type?.split("/")[1],
            statusType: STATUS_TYPES.rejected,
            errorMessage: getReduxActionErrorMessage(action)
          };
        }
      })
  }
});

export const cameraACLActions = cameraACLSlice.actions;
export const cameraACLState = (state) => state.cameraACL;
export default cameraACLSlice.reducer;


